import React from "react";
import "./TPMSFraud.css";

/* ============================================================
   IMAGES – apni image ke paths yahan badlo (public/TPMS_img/ me rakho)
   ============================================================ */
const IMAGES = {
  overview: "/ImagesNew/reducetollfraud.png",
  evidence: "/ImagesNew/imageevidence.jpeg",
};

/* ============================================================
   CONTENT  (client ka content – yahin se edit karo)
   ============================================================ */
const HEAD = {
  badge: "REVENUE ASSURANCE",
  title: "Reducing Toll Fraud and Revenue Leakage",
  subtitle: "Independent Vehicle Evidence for Greater Transparency",
  paragraphs: [
    "Revenue leakage is a significant concern for toll operations. Manual errors, incorrect categorization, unauthorized exemptions, and discrepancies in transaction records can affect collection accountability.",
    "TPMS introduces an independent video-based monitoring layer that enables toll operators to compare actual observed vehicle movements with toll collection records.",
  ],
};

const IDENTIFY = {
  title: "What TPMS Helps Operators Identify",
  items: [
    { icon: "ticketOff", text: "Vehicles passing without corresponding toll transaction records" },
    { icon: "alert", text: "Suspicious or excessive vehicle exemptions" },
    { icon: "shield", text: "Potential misuse of exempted vehicle categories" },
    { icon: "truck", text: "Incorrect vehicle classifications" },
    { icon: "compare", text: "Differences between recorded vehicle movements and reported transactions" },
    { icon: "camera", text: "Vehicle-related disputes requiring visual verification" },
  ],
};

const EVIDENCE = {
  title: "Image and Video Evidence for Every Supported Investigation",
  paragraphs: [
    "Rather than relying solely on manually entered transaction information, operators can use captured images and video footage to investigate potential discrepancies.",
    "This strengthens auditing, helps discourage unauthorized activity, and provides greater transparency across toll collection operations.",
  ],
};

const STRAP = ["Greater Visibility.", "Better Accountability.", "Stronger Revenue Assurance."];

/* ============================================================
   ICONS
   ============================================================ */
const ICONS = {
  ticketOff: (
    <>
      <path d="M2 9a3 3 0 0 1 0 6v2a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2v-2a3 3 0 0 1 0-6V7a2 2 0 0 0-2-2H4a2 2 0 0 0-2 2Z" />
      <path d="M13 5v2M13 11v2M13 17v2" />
    </>
  ),
  alert: (
    <>
      <path d="m21.73 18-8-14a2 2 0 0 0-3.48 0l-8 14A2 2 0 0 0 4 21h16a2 2 0 0 0 1.73-3Z" />
      <path d="M12 9v4M12 17h.01" />
    </>
  ),
  shield: (
    <>
      <path d="M20 13c0 5-3.5 7.5-7.66 8.95a1 1 0 0 1-.67-.01C7.5 20.5 4 18 4 13V6a1 1 0 0 1 1-1c2 0 4.5-1.2 6.24-2.72a1.17 1.17 0 0 1 1.52 0C14.51 3.81 17 5 19 5a1 1 0 0 1 1 1z" />
      <path d="M12 8v4M12 16h.01" />
    </>
  ),
  truck: (
    <>
      <path d="M14 18V6a2 2 0 0 0-2-2H4a2 2 0 0 0-2 2v11a1 1 0 0 0 1 1h2" />
      <path d="M15 18H9" />
      <path d="M19 18h2a1 1 0 0 0 1-1v-3.65a1 1 0 0 0-.22-.624l-3.48-4.35A1 1 0 0 0 17.52 8H14" />
      <circle cx="17" cy="18" r="2" />
      <circle cx="7" cy="18" r="2" />
    </>
  ),
  compare: (
    <>
      <path d="M16 3h5v5M8 21H3v-5" />
      <path d="M21 3 14 10M3 21l7-7" />
    </>
  ),
  camera: (
    <>
      <path d="M14.5 4h-5L7 7H4a2 2 0 0 0-2 2v9a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2V9a2 2 0 0 0-2-2h-3l-2.5-3z" />
      <circle cx="12" cy="13" r="3" />
    </>
  ),
  eye: (
    <>
      <path d="M2.06 12.35a1 1 0 0 1 0-.7 10.75 10.75 0 0 1 19.88 0 1 1 0 0 1 0 .7 10.75 10.75 0 0 1-19.88 0" />
      <circle cx="12" cy="12" r="3" />
    </>
  ),
  check: <path d="m20 6-11 11-5-5" />,
  trend: (
    <>
      <path d="m22 7-8.5 8.5-5-5L2 17" />
      <path d="M16 7h6v6" />
    </>
  ),
};

const Icon = ({ name, size = 24 }) => (
  <svg
    viewBox="0 0 24 24"
    width={size}
    height={size}
    fill="none"
    stroke="currentColor"
    strokeWidth="1.8"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    {ICONS[name]}
  </svg>
);

/* dark rounded image frame (same as other sections) */
const Frame = ({ src, alt, tag }) => (
  <div className="tf__frame">
    <div className="tf__frame-inner">
      {tag && <span className="tf__tag">{tag}</span>}
      <img src={src} alt={alt} loading="lazy" className="tf__img" />
    </div>
  </div>
);

/* ============================================================
   SECTION
   ============================================================ */
const TPMSFraud = () => (
  <section id="revenue" className="tf">
    <div className="tf__container">
      {/* ---------- intro ---------- */}
      <div className="tf__intro">
        <div className="tf__intro-text">
          <span className="tf__pill">{HEAD.badge}</span>
          <h2 className="tf__title">{HEAD.title}</h2>
          <p className="tf__subtitle">{HEAD.subtitle}</p>
          <span className="tf__underline" />
          {HEAD.paragraphs.map((p) => (
            <p key={p} className="tf__para">
              {p}
            </p>
          ))}
        </div>
        <Frame src={IMAGES.overview} alt="TPMS revenue monitoring" tag="REVENUE MONITORING" />
      </div>

      {/* ---------- what TPMS helps identify ---------- */}
      <div className="tf__identify">
        <h3 className="tf__h3">{IDENTIFY.title}</h3>
        <ul className="tf__grid">
          {IDENTIFY.items.map((it, i) => (
            <li className="tf__card" key={it.text}>
              <span className="tf__card-num">{String(i + 1).padStart(2, "0")}</span>
              <span className="tf__icon">
                <Icon name={it.icon} />
              </span>
              <p>{it.text}</p>
            </li>
          ))}
        </ul>
      </div>

      {/* ---------- evidence ---------- */}
      <div className="tf__evidence">
        <div className="tf__evidence-text">
          <span className="tf__ev-icon">
            <Icon name="eye" size={26} />
          </span>
          <h3>{EVIDENCE.title}</h3>
          {EVIDENCE.paragraphs.map((p) => (
            <p key={p}>{p}</p>
          ))}
        </div>
        <Frame src={IMAGES.evidence} alt="Image and video evidence" tag="VIDEO EVIDENCE" />
      </div>

      {/* ---------- strap ---------- */}
      <ul className="tf__strap">
        {STRAP.map((s) => (
          <li key={s}>
            <span aria-hidden="true">
              <Icon name="check" size={16} />
            </span>
            {s}
          </li>
        ))}
      </ul>
    </div>
  </section>
);

export default TPMSFraud;